import type { DailyRow, Mall, PlBlock, PlResponse } from "../../../types/pl";
import { StackedBarChart } from "../../../components/StackedBarChart";
import { Waterfall, type WaterfallItem } from "../../../components/Waterfall";
import { CompBar, type CompItem } from "../../../components/CompBar";
import { BasisBadge } from "../../../components/BasisBadge";
import { delta, num, pct, profitColor, roasFmt, yen } from "../../../lib/plFormat";

const MALLS: Mall[] = ["shopify", "rakuten", "amazon"];
const MALL_LABEL: Record<Mall, string> = { shopify: "Shopify", rakuten: "楽天市場", amazon: "Amazon" };
const MALL_COLOR: Record<Mall, string> = { shopify: "#5a8f4e", rakuten: "#c43d3a", amazon: "#e0922a" };

const COMP_DEFS = [
  { key: "cogs", label: "商品原価", color: "var(--pl-cost-cogs)" },
  { key: "fees", label: "手数料", color: "var(--pl-cost-fee)" },
  { key: "ship", label: "送料・物流", color: "var(--pl-cost-ship)" },
  { key: "pts", label: "ポイント・クーポン", color: "var(--pl-cost-pts)" },
  { key: "ad", label: "広告費", color: "var(--pl-cost-ad)" },
  { key: "profit", label: "貢献利益", color: "var(--pl-cost-profit)" },
];

function compItems(block: PlBlock): CompItem[] {
  const sales = block.sales.value || 1;
  const values: Record<string, number> = {
    cogs: block.cogs.value,
    fees: block.mall_fee.value + block.payment_fee.value,
    ship: block.shipping.value,
    pts: block.points_coupon.value,
    ad: block.ad_spend.value,
    profit: Math.max(0, block.contribution),
  };
  return COMP_DEFS.map((c) => ({
    key: c.key,
    label: c.label,
    color: c.color,
    widthPct: (values[c.key] / sales) * 100,
    pctText: pct((c.key === "profit" ? block.contribution : values[c.key]) / sales),
  }));
}

interface OverviewTabProps {
  data: PlResponse;
  chartDaily: DailyRow[];
  chartFrom: string;
  chartTo: string;
  hoverDay: string | null;
  onHoverDay: (d: string | null) => void;
}

export function OverviewTab({ data, chartDaily, chartFrom, chartTo }: OverviewTabProps) {
  const { total, compare, malls, fixed_costs, side_costs, meta } = data;
  const prevTotal = compare?.total;

  const kpis: { label: string; value: string; delta: { text: string; color: string } }[] = [
    { label: "売上", value: yen(total.sales.value), delta: delta(total.sales.value, prevTotal?.sales.value) },
    { label: "貢献利益", value: yen(total.contribution), delta: delta(total.contribution, prevTotal?.contribution) },
    { label: "利益率", value: pct(total.margin), delta: delta(total.margin ?? 0, prevTotal?.margin ?? null, { pt: true }) },
    { label: "広告費", value: yen(total.ad_spend.value), delta: delta(total.ad_spend.value, prevTotal?.ad_spend.value, { invert: true }) },
    { label: "ROAS", value: roasFmt(total.roas), delta: delta(total.roas ?? 0, prevTotal?.roas ?? null) },
    { label: "注文数", value: num(total.orders), delta: delta(total.orders, prevTotal?.orders) },
    { label: "客単価", value: yen(total.aov ?? 0), delta: delta(total.aov ?? 0, prevTotal?.aov ?? null) },
  ];

  const sales = total.sales.value || 1;
  let cum = 0;
  const waterfallItems: WaterfallItem[] = [
    { label: "売上高", amountText: yen(total.sales.value), pctText: "100%", leftPct: 0, widthPct: 100, color: "#1f2523", bold: true },
  ];
  const costRows: [string, number, string][] = [
    ["商品原価", total.cogs.value, "var(--pl-cost-cogs)"],
    ["モール手数料", total.mall_fee.value, "var(--pl-cost-fee)"],
    ["決済手数料", total.payment_fee.value, "var(--pl-cost-fee)"],
    ["送料・物流費", total.shipping.value, "var(--pl-cost-ship)"],
    ["ポイント・クーポン", total.points_coupon.value, "var(--pl-cost-pts)"],
    ["広告費", total.ad_spend.value, "var(--pl-cost-ad)"],
  ];
  costRows.forEach(([label, v, color]) => {
    waterfallItems.push({
      label,
      amountText: `−${yen(v)}`,
      pctText: pct(v / sales),
      leftPct: (cum / sales) * 100,
      widthPct: (v / sales) * 100,
      color,
    });
    cum += v;
  });
  waterfallItems.push({
    label: "貢献利益",
    amountText: yen(total.contribution),
    pctText: pct(total.contribution / sales),
    leftPct: (cum / sales) * 100,
    widthPct: Math.max(0, (total.contribution / sales) * 100),
    color: "var(--pl-cost-profit)",
    bold: true,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="pl-kpi-grid">
        {kpis.map((k) => (
          <div className="pl-kpi-card" key={k.label}>
            <span className="pl-kpi-label">{k.label}</span>
            <span className="pl-kpi-value">{k.value}</span>
            <span className="pl-kpi-delta" style={{ color: k.delta.color }}>
              {k.delta.text}
            </span>
          </div>
        ))}
      </div>

      <div className="pl-two-col">
        <div className="pl-card">
          <div className="pl-card-head">
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="pl-card-title">モール別売上と利益</span>
            </div>
            <div style={{ display: "flex", gap: 14, fontSize: 12, color: "var(--pl-muted)", flexWrap: "wrap" }}>
              {MALLS.map((m) => (
                <span key={m} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: MALL_COLOR[m] }} />
                  {MALL_LABEL[m]}
                </span>
              ))}
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#1f2523" }} />
                貢献利益
              </span>
            </div>
          </div>
          <StackedBarChart daily={chartDaily} from={chartFrom} to={chartTo} metric="sales" showProfitDot />
        </div>

        <div className="pl-card">
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="pl-card-title">損益ブレークダウン</span>
            <span className="pl-card-sub">売上高から貢献利益まで ・ 全モール合計</span>
          </div>
          <Waterfall items={waterfallItems} />
        </div>
      </div>

      <div className="pl-mall-cards">
        {MALLS.map((m) => {
          const block = malls[m];
          const status = meta.malls.find((x) => x.id === m)?.status ?? "live";
          return (
            <div className="pl-mall-card" style={{ borderTopColor: MALL_COLOR[m] }} key={m}>
              <div className="pl-mall-card-head">
                <span className="pl-card-title">
                  {MALL_LABEL[m]}
                  {status === "not_connected" && (
                    <span className="pl-badge pl-badge--unconfirmed" style={{ marginLeft: 8 }}>
                      未連携
                    </span>
                  )}
                </span>
                <span className="pl-card-sub">売上構成比 {pct(block.sales.value / sales)}</span>
              </div>
              <div className="pl-mall-mini-grid">
                <div className="pl-mall-mini-item">
                  <span className="pl-mall-mini-label">売上</span>
                  <span className="pl-mall-mini-value">
                    {yen(block.sales.value)} <BasisBadge basis={block.sales.basis} note={block.sales.note} />
                  </span>
                </div>
                <div className="pl-mall-mini-item">
                  <span className="pl-mall-mini-label">貢献利益</span>
                  <span className="pl-mall-mini-value" style={{ color: profitColor(block.contribution) }}>
                    {yen(block.contribution)}
                  </span>
                </div>
                <div className="pl-mall-mini-item">
                  <span className="pl-mall-mini-label">利益率</span>
                  <span className="pl-mall-mini-value--sm">{pct(block.margin)}</span>
                </div>
                <div className="pl-mall-mini-item">
                  <span className="pl-mall-mini-label">広告費 / ROAS</span>
                  <span className="pl-mall-mini-value--sm">
                    {yen(block.ad_spend.value)} ・ {roasFmt(block.roas)}
                  </span>
                </div>
                <div className="pl-mall-mini-item">
                  <span className="pl-mall-mini-label">注文数</span>
                  <span className="pl-mall-mini-value--sm">{num(block.orders)}</span>
                </div>
                <div className="pl-mall-mini-item">
                  <span className="pl-mall-mini-label">客単価</span>
                  <span className="pl-mall-mini-value--sm">{yen(block.aov ?? 0)}</span>
                </div>
              </div>
              <CompBar items={compItems(block)} showLegend={false} />
              {block.warnings && block.warnings.length > 0 && (
                <div className="pl-warning">{block.warnings.join(" ／ ")}</div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: "var(--pl-muted)", marginTop: -8 }}>
        {COMP_DEFS.map((c) => (
          <span key={c.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: c.color }} />
            {c.label}
          </span>
        ))}
      </div>

      {(fixed_costs.length > 0 || side_costs.length > 0) && (
        <div className="pl-card">
          <span className="pl-card-title">固定費・別枠費用（参考・貢献利益には含めない）</span>
          <div className="pl-aux-cards">
            {fixed_costs.map((f, i) => (
              <div className="pl-aux-card" key={`fixed-${i}`}>
                <span className="pl-aux-card-title">{f.label}</span>
                <span>
                  {yen(f.amount)} <BasisBadge basis={f.basis} />
                </span>
                <span className="pl-aux-card-note">{f.month} ・ 固定費（参考）</span>
              </div>
            ))}
            {side_costs.map((s, i) => (
              <div className="pl-aux-card" key={`side-${i}`}>
                <span className="pl-aux-card-title">{s.label}</span>
                <span>
                  {yen(s.amount)} <BasisBadge basis={s.basis} />
                </span>
                <span className="pl-aux-card-note">{s.month} ・ 着払い等（貢献利益に含めない）</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
